import React, { useState } from 'react';
import { Bell, Mail, Smartphone, MessageSquare } from 'lucide-react';
import { Switch } from '../ui/switch';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';

const NotificationSettings = () => {
  const [notifications, setNotifications] = useState({
    salesAlerts: { email: true, push: true, sms: false },
    socialInteractions: { email: true, push: true, sms: false },
    platformUpdates: { email: true, push: false, sms: false },
    contentApproval: { email: true, push: true, sms: false },
    earnings: { email: true, push: true, sms: true },
    marketing: { email: false, push: false, sms: false }
  });

  const updateNotification = (category, type, value) => {
    setNotifications(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [type]: value
      }
    }));
  };

  const notificationCategories = [
    {
      key: 'salesAlerts',
      title: 'Sales & Downloads',
      description: 'When someone purchases or downloads your content',
      icon: <Bell className="w-5 h-5" />
    },
    {
      key: 'socialInteractions',
      title: 'Comments & Likes',
      description: 'When users interact with your content',
      icon: <MessageSquare className="w-5 h-5" />
    },
    {
      key: 'contentApproval',
      title: 'Content Status',
      description: 'Updates on your content approval status',
      icon: <Bell className="w-5 h-5" />
    },
    {
      key: 'earnings',
      title: 'Earnings & Payouts',
      description: 'Payment confirmations and earning updates',
      icon: <Bell className="w-5 h-5" />
    },
    {
      key: 'platformUpdates',
      title: 'Platform Updates',
      description: 'New features and important announcements',
      icon: <Bell className="w-5 h-5" />
    },
    {
      key: 'marketing',
      title: 'Marketing Communications',
      description: 'Promotional offers and marketing content',
      icon: <Mail className="w-5 h-5" />
    }
  ];

  return (
    <div className="space-y-6">
      {/* Notification Preferences Table */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Notification Preferences</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-600">
                  <th className="text-left text-white font-medium py-3">Notification Type</th>
                  <th className="text-center text-white font-medium py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email
                    </div>
                  </th>
                  <th className="text-center text-white font-medium py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Bell className="w-4 h-4" />
                      Push
                    </div>
                  </th>
                  <th className="text-center text-white font-medium py-3 px-4">
                    <div className="flex items-center justify-center gap-2">
                      <Smartphone className="w-4 h-4" />
                      SMS
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {notificationCategories.map((category) => (
                  <tr key={category.key} className="border-b border-gray-700/50">
                    <td className="py-4">
                      <div className="flex items-center gap-3">
                        <div className="text-yellow-400">
                          {category.icon}
                        </div>
                        <div>
                          <h4 className="text-white font-medium">{category.title}</h4>
                          <p className="text-gray-400 text-sm">{category.description}</p>
                        </div>
                      </div>
                    </td>
                    <td className="text-center px-4">
                      <Switch
                        checked={notifications[category.key]?.email || false}
                        onCheckedChange={(checked) => updateNotification(category.key, 'email', checked)}
                      />
                    </td>
                    <td className="text-center px-4">
                      <Switch
                        checked={notifications[category.key]?.push || false}
                        onCheckedChange={(checked) => updateNotification(category.key, 'push', checked)}
                      />
                    </td>
                    <td className="text-center px-4">
                      <Switch
                        checked={notifications[category.key]?.sms || false}
                        onCheckedChange={(checked) => updateNotification(category.key, 'sms', checked)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Quiet Hours */}
      <Card className="bg-gray-800 border-gray-700">
        <CardHeader>
          <CardTitle className="text-white">Quiet Hours</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-gray-400 text-sm">Set times when you don't want to receive notifications</p>
          
          <div className="flex items-center justify-between">
            <div>
              <h4 className="text-white font-medium">Enable Quiet Hours</h4>
              <p className="text-gray-400 text-sm">Pause notifications during specified times</p>
            </div>
            <Switch />
          </div>

          <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-600">
            <div>
              <label className="text-white text-sm font-medium">Start Time</label>
              <input 
                type="time" 
                className="w-full mt-1 bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                defaultValue="22:00"
              />
            </div>
            <div>
              <label className="text-white text-sm font-medium">End Time</label>
              <input 
                type="time" 
                className="w-full mt-1 bg-gray-700 border border-gray-600 rounded-md px-3 py-2 text-white"
                defaultValue="08:00"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Custom Preferences */}
      <Card className="bg-gradient-to-r from-purple-900/30 to-pink-900/30 border border-purple-500/20">
        <CardContent className="pt-6">
          <div className="space-y-4">
            <h3 className="text-white font-semibold">Custom Notification Rules</h3>
            <p className="text-gray-400 text-sm">
              Set up custom rules for specific scenarios like high-value sales or viral content.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-800/50 p-3 rounded-lg">
                <h4 className="text-purple-400 font-medium">High-Value Sales</h4>
                <p className="text-gray-400 text-sm">Sales over $50</p>
              </div>
              <div className="bg-gray-800/50 p-3 rounded-lg">
                <h4 className="text-pink-400 font-medium">Viral Content</h4>
                <p className="text-gray-400 text-sm">1000+ views in 24h</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationSettings;
